import { useState, useEffect, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { useSettings } from '@/hooks/useSettings';
import { db } from '@/lib/firebase';
import { doc, getDoc, setDoc, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { sanitizeObject } from '@/lib/sanitize';

interface FormData {
  name: string;
  email: string;
  phone: string;
  service: string;
  message: string;
  website?: string; // Honeypot
}

interface SocialLink {
  icon: string;
  label: string;
  handle: string;
  href: string;
  color: string;
}

interface ContactContent {
  email: string;
  response: {
    label: string;
    value: string;
    days: string;
  };
  service: {
    label: string;
    value: string;
    languages: string;
  };
  socialLinks: SocialLink[];
  calendar: {
    badge: string;
    titlePrefix: string;
    titleHighlight: string;
    subtitle: string;
    cta: string;
    link: string;
  };
}

const ContactPage = () => {
  const { t } = useTranslation();
  const { role } = useAuth();
  const isAdmin = role === 'admin';
  const { settings } = useSettings();

  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
    website: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const [content, setContent] = useState<ContactContent>({
    email: 'info@smartbusinessdigital.com',
    response: {
      label: 'Tiempo de Respuesta',
      value: '24-48 Horas',
      days: 'Días laborables'
    },
    service: {
      label: 'Área de Servicio',
      value: 'Global',
      languages: 'Español e Inglés'
    },
    socialLinks: [
      { icon: 'ri-instagram-line', label: 'Instagram', handle: '@smartbusinessdigital', href: 'https://instagram.com', color: '#FF2DAA' },
      { icon: 'ri-facebook-circle-line', label: 'Facebook', handle: 'Smart Business Digital', href: 'https://facebook.com', color: '#8c52ff' },
      { icon: 'ri-tiktok-line', label: 'TikTok', handle: '@smartbusinessdigital', href: 'https://tiktok.com', color: '#00c2ff' },
      { icon: 'ri-youtube-line', label: 'YouTube', handle: 'Smart Business Digital', href: 'https://youtube.com', color: '#FF2DAA' },
      { icon: 'ri-linkedin-box-line', label: 'LinkedIn', handle: 'Cathy Roldán', href: 'https://linkedin.com', color: '#00c2ff' },
    ],
    calendar: {
      badge: 'AGENDA UNA LLAMADA',
      titlePrefix: 'Reserva una',
      titleHighlight: 'Asesoría Gratuita',
      subtitle: 'Hablemos de tus objetivos y cómo podemos ayudarte a escalar tu negocio.',
      cta: 'Reservar Llamada',
      link: 'https://stan.store'
    }
  });

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const docRef = doc(db, 'landing_content', 'contact');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setContent(docSnap.data() as ContactContent);
        } else {
          await setDoc(docRef, content);
        }
      } catch (error) {
        console.error('Error fetching contact content:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    try {
      await setDoc(doc(db, 'landing_content', 'contact'), content);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving contact content:', error);
      alert('Error al guardar los cambios.');
    }
  };

  const services = [
    'Social Media Management',
    'Content Creation',
    'Website Design (Wix)',
    'Branding & Design',
    'AI Automation & Funnels',
    'Email Marketing',
    t('contact.form.services.consulting'),
    t('contact.form.services.other'),
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Honeypot check
    if (formData.website) {
      console.log('Bot detected!');
      setSubmitting(false);
      return; // Silently ignore
    }

    // Rate limiting check
    const lastSubmit = localStorage.getItem('last_contact_submit');
    const now = Date.now();
    if (lastSubmit && now - parseInt(lastSubmit) < 60000) { // 1 minute
      setError('Por favor, espera un minuto antes de enviar otro mensaje.');
      return;
    }

    if (!formData.name || !formData.email || !formData.message) {
      setError(t('contact.form.errors.required'));
      return;
    }
    if (formData.message.length > 500) {
      setError(t('contact.form.errors.maxLength'));
      return;
    }
    setError('');
    setSubmitting(true);

    // Sanitizar datos
    const sanitizedData = sanitizeObject(formData);

    try {
      // 1. Obtener IP del cliente
      let ip = 'Unknown';
      try {
        const ipRes = await fetch('https://api.ipify.org?format=json');
        const ipData = await ipRes.json();
        ip = ipData.ip;
      } catch (e) {
        console.error('Error fetching IP:', e);
      }

      // 2. Guardar en Firestore
      await addDoc(collection(db, 'contact_messages'), {
        ...sanitizedData,
        ip: ip,
        createdAt: serverTimestamp()
      });

      // 3. Enviar a la API externa (Readdy)
      const body = new URLSearchParams({
        name: sanitizedData.name,
        email: sanitizedData.email,
        phone: sanitizedData.phone,
        service: sanitizedData.service,
        message: sanitizedData.message,
      });
      await fetch('https://readdy.ai/api/form/d7a39rr86jhav3jpfu9g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      localStorage.setItem('last_contact_submit', Date.now().toString());
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSocialChange = (index: number, field: keyof SocialLink, value: string) => {
    const newLinks = content.socialLinks.map((link, i) => 
      i === index ? { ...link, [field]: value } : link
    );
    setContent({ ...content, socialLinks: newLinks });
  };

  return (
    <div className="min-h-screen bg-[#030006] text-white">
      {/* Botones de Control para Admin */}
      {isAdmin && (
        <div className="fixed top-24 right-4 z-50 flex gap-2">
          {!isEditing ? (
            <button 
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2 text-sm shadow-lg"
            >
              <i className="ri-edit-line" />
              Editar Página
            </button>
          ) : (
            <>
              <button 
                onClick={handleSave}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2 text-sm shadow-lg"
              >
                <i className="ri-save-line" />
                Guardar
              </button>
              <button 
                onClick={() => setIsEditing(false)}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium transition-colors flex items-center gap-2 text-sm shadow-lg"
              >
                <i className="ri-close-line" />
                Cancelar
              </button>
            </>
          )}
        </div>
      )}

      {/* Hero */}
      <section className="relative pt-36 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_20%,rgba(140,82,255,0.18)_0%,transparent_60%)] pointer-events-none" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_80%,rgba(0,194,255,0.10)_0%,transparent_60%)] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center relative z-10">
          <p className="text-[#FF2DAA] text-xs font-bold uppercase tracking-widest mb-4">{t('contact.hero.badge')}</p>
          <h1 className="text-4xl md:text-6xl font-black mb-6 leading-tight">
            {t('contact.hero.title')}
          </h1>
          <p className="text-white/60 text-lg max-w-2xl mx-auto leading-relaxed">
            {t('contact.hero.subtitle')}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 pb-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">

            {/* Left: Info */}
            <div className="lg:col-span-2 flex flex-col gap-8">

              {/* Contact Info Cards */}
              <div className="space-y-4">
                <div className="bg-white/3 border border-white/8 rounded-2xl p-6 flex items-start gap-4 hover:border-[#8c52ff]/40 transition-all duration-300">
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-gradient-to-br from-[#8c52ff]/30 to-[#8c52ff]/10 border border-[#8c52ff]/30 flex-shrink-0">
                    <i className="ri-mail-line text-[#8c52ff] text-lg" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white/40 text-xs uppercase tracking-widest mb-1">{t('contact.info.email.label')}</p>
                    {isEditing ? (
                      <input 
                        type="email" 
                        value={content.email} 
                        onChange={(e) => setContent({...content, email: e.target.value})}
                        className="w-full bg-white/5 border border-purple-500/50 rounded px-2 py-1 text-white text-sm focus:outline-none"
                      />
                    ) : (
                      <a
                        href={`mailto:${content.email}`}
                        className="text-white font-semibold text-sm hover:text-[#00c2ff] transition-colors duration-300"
                      >
                        {content.email}
                      </a>
                    )}
                  </div>
                </div>

                <div className="bg-white/3 border border-white/8 rounded-2xl p-6 flex items-start gap-4 hover:border-[#FF2DAA]/40 transition-all duration-300">
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-gradient-to-br from-[#FF2DAA]/30 to-[#FF2DAA]/10 border border-[#FF2DAA]/30 flex-shrink-0">
                    <i className="ri-time-line text-[#FF2DAA] text-lg" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white/40 text-xs uppercase tracking-widest mb-1">{t('contact.info.response.label')}</p>
                    {isEditing ? (
                      <div className="space-y-1">
                        <input 
                          type="text" 
                          value={content.response.value} 
                          onChange={(e) => setContent({...content, response: {...content.response, value: e.target.value}})}
                          className="w-full bg-white/5 border border-purple-500/50 rounded px-2 py-1 text-white text-sm font-semibold focus:outline-none"
                        />
                        <input 
                          type="text" 
                          value={content.response.days} 
                          onChange={(e) => setContent({...content, response: {...content.response, days: e.target.value}})}
                          className="w-full bg-white/5 border border-purple-500/50 rounded px-2 py-1 text-white/40 text-xs focus:outline-none"
                        />
                      </div>
                    ) : (
                      <>
                        <p className="text-white font-semibold text-sm">{content.response.value}</p>
                        <p className="text-white/40 text-xs mt-0.5">{content.response.days}</p>
                      </>
                    )}
                  </div>
                </div>

                <div className="bg-white/3 border border-white/8 rounded-2xl p-6 flex items-start gap-4 hover:border-[#00c2ff]/40 transition-all duration-300">
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-gradient-to-br from-[#00c2ff]/30 to-[#00c2ff]/10 border border-[#00c2ff]/30 flex-shrink-0">
                    <i className="ri-global-line text-[#00c2ff] text-lg" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white/40 text-xs uppercase tracking-widest mb-1">{t('contact.info.service.label')}</p>
                    {isEditing ? (
                      <div className="space-y-1">
                        <input 
                          type="text" 
                          value={content.service.value} 
                          onChange={(e) => setContent({...content, service: {...content.service, value: e.target.value}})}
                          className="w-full bg-white/5 border border-purple-500/50 rounded px-2 py-1 text-white text-sm font-semibold focus:outline-none"
                        />
                        <input 
                          type="text" 
                          value={content.service.languages} 
                          onChange={(e) => setContent({...content, service: {...content.service, languages: e.target.value}})}
                          className="w-full bg-white/5 border border-purple-500/50 rounded px-2 py-1 text-white/40 text-xs focus:outline-none"
                        />
                      </div>
                    ) : (
                      <>
                        <p className="text-white font-semibold text-sm">{content.service.value}</p>
                        <p className="text-white/40 text-xs mt-0.5">{content.service.languages}</p>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div>
                <h3 className="text-white font-bold text-base mb-4 uppercase tracking-widest text-xs text-white/40">
                  {t('contact.social.title')}
                </h3>
                <div className="space-y-3">
                  {content.socialLinks.map((s, i) => {
                    let dynamicHref = s.href;
                    if (s.icon.includes('instagram')) dynamicHref = settings.instagramUrl || s.href;
                    if (s.icon.includes('facebook')) dynamicHref = settings.facebookUrl || s.href;
                    if (s.icon.includes('tiktok')) dynamicHref = settings.tiktokUrl || s.href;
                    if (s.icon.includes('linkedin')) dynamicHref = settings.linkedinUrl || s.href;
                    
                    return (
                    <div key={i} className="flex flex-col gap-2">
                      <a
                        href={dynamicHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-4 bg-white/3 border border-white/8 rounded-xl px-4 py-3 hover:border-white/20 hover:-translate-x-1 transition-all duration-300 cursor-pointer group"
                      >
                        <div
                          className="w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0"
                          style={{ backgroundColor: `${s.color}20`, border: `1px solid ${s.color}40` }}
                        >
                          <i className={`${s.icon} text-base`} style={{ color: s.color }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white/80 text-sm font-semibold group-hover:text-white transition-colors duration-300">{s.label}</p>
                          <p className="text-white/40 text-xs truncate">{s.handle}</p>
                        </div>
                        <i className="ri-arrow-right-line text-white/20 group-hover:text-white/60 transition-colors duration-300" />
                      </a>
                      {isEditing && (
                        <div className="grid grid-cols-2 gap-2 mt-1">
                          <input 
                            type="text" 
                            value={s.handle} 
                            onChange={(e) => handleSocialChange(i, 'handle', e.target.value)}
                            className="w-full bg-white/5 border border-purple-500/50 rounded px-2 py-1 text-white text-xs focus:outline-none"
                            placeholder="Usuario (ej: @smart...)"
                          />
                          <input 
                            type="text" 
                            value={s.href} 
                            onChange={(e) => handleSocialChange(i, 'href', e.target.value)}
                            className="w-full bg-white/5 border border-purple-500/50 rounded px-2 py-1 text-white text-xs focus:outline-none"
                            placeholder="URL del enlace"
                          />
                        </div>
                      )}
                    </div>
                  )})}
                </div>
              </div>
            </div>

            {/* Right: Form */}
            <div className="lg:col-span-3">
              <div className="bg-white/3 border border-white/8 rounded-3xl p-8 md:p-10">
                {submitted ? (
                  <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className="w-20 h-20 flex items-center justify-center rounded-full bg-gradient-to-br from-[#8c52ff] to-[#00c2ff] mb-6">
                      <i className="ri-check-line text-white text-3xl" />
                    </div>
                    <h3 className="text-white text-2xl font-black mb-3">{t('contact.success.title')}</h3>
                    <p className="text-white/60 text-base max-w-sm leading-relaxed">
                      {t('contact.success.subtitle')}
                    </p>
                    <button
                      onClick={() => { setSubmitted(false); setFormData({ name: '', email: '', phone: '', service: '', message: '' }); }}
                      className="whitespace-nowrap mt-8 bg-white/10 border border-white/20 text-white text-sm font-semibold px-6 py-3 rounded-full hover:bg-white/15 transition-all duration-300 cursor-pointer"
                    >
                      {t('contact.success.cta')}
                    </button>
                  </div>
                ) : (
                  <>
                    <h2 className="text-white text-2xl font-black mb-2">{t('contact.form.title')}</h2>
                    <p className="text-white/50 text-sm mb-8">{t('contact.form.subtitle')}</p>

                    {error && (
                      <div className="bg-[#FF2DAA]/10 border border-[#FF2DAA]/30 rounded-xl px-4 py-3 mb-6 flex items-center gap-2">
                        <i className="ri-error-warning-line text-[#FF2DAA]" />
                        <p className="text-[#FF2DAA] text-sm">{error}</p>
                      </div>
                    )}

                    <form
                      id="contact-form"
                      data-readdy-form
                      onSubmit={handleSubmit}
                      className="space-y-5"
                    >
                      {/* Honeypot */}
                      <div className="hidden" aria-hidden="true">
                        <input
                          type="text"
                          name="website"
                          value={formData.website}
                          onChange={handleChange}
                          tabIndex={-1}
                          autoComplete="off"
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-white/60 text-xs font-semibold uppercase tracking-wider mb-2">
                            {t('contact.form.fields.name.label')} <span className="text-[#FF2DAA]">*</span>
                          </label>
                          <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            placeholder={t('contact.form.fields.name.placeholder')}
                            required
                            className="w-full bg-white/5 border border-white/15 rounded-xl px-4 py-3 text-sm text-white placeholder-white/25 focus:outline-none focus:border-[#8c52ff]/60 transition-colors duration-300"
                          />
                        </div>
                        <div>
                          <label className="block text-white/60 text-xs font-semibold uppercase tracking-wider mb-2">
                            {t('contact.form.fields.email.label')} <span className="text-[#FF2DAA]">*</span>
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="tu@email.com"
                            required
                            className="w-full bg-white/5 border border-white/15 rounded-xl px-4 py-3 text-sm text-white placeholder-white/25 focus:outline-none focus:border-[#8c52ff]/60 transition-colors duration-300"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-white/60 text-xs font-semibold uppercase tracking-wider mb-2">
                            {t('contact.form.fields.phone.label')}
                          </label>
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="+1 (555) 000-0000"
                            className="w-full bg-white/5 border border-white/15 rounded-xl px-4 py-3 text-sm text-white placeholder-white/25 focus:outline-none focus:border-[#8c52ff]/60 transition-colors duration-300"
                          />
                        </div>
                        <div>
                          <label className="block text-white/60 text-xs font-semibold uppercase tracking-wider mb-2">
                            {t('contact.form.fields.service.label')}
                          </label>
                          <select
                            name="service"
                            value={formData.service}
                            onChange={handleChange}
                            className="w-full bg-white/5 border border-white/15 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-[#8c52ff]/60 transition-colors duration-300 cursor-pointer appearance-none"
                          >
                            <option value="" className="bg-[#0a0010]">{t('contact.form.fields.service.placeholder')}</option>
                            {services.map((s) => (
                              <option key={s} value={s} className="bg-[#0a0010]">{s}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-white/60 text-xs font-semibold uppercase tracking-wider mb-2">
                          {t('contact.form.fields.message.label')} <span className="text-[#FF2DAA]">*</span>
                        </label>
                        <textarea
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          placeholder={t('contact.form.fields.message.placeholder')}
                          required
                          maxLength={500}
                          rows={5}
                          className="w-full bg-white/5 border border-white/15 rounded-xl px-4 py-3 text-sm text-white placeholder-white/25 focus:outline-none focus:border-[#8c52ff]/60 transition-colors duration-300 resize-none"
                        />
                        <p className="text-white/25 text-xs mt-1 text-right">{formData.message.length}/500</p>
                      </div>

                      <button
                        type="submit"
                        disabled={submitting}
                        className="whitespace-nowrap w-full flex items-center justify-center gap-3 bg-gradient-to-r from-[#8c52ff] to-[#00c2ff] text-white font-bold py-4 rounded-xl hover:opacity-90 transition-opacity duration-300 cursor-pointer disabled:opacity-60 text-base"
                      >
                        {submitting ? (
                          <>
                            <i className="ri-loader-4-line animate-spin" />
                            {t('contact.form.cta.sending')}
                          </>
                        ) : (
                          <>
                            <i className="ri-send-plane-line" />
                            {t('contact.form.cta.label')}
                          </>
                        )}
                      </button>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Calendar Booking Section */}
      <section className="py-20 relative overflow-hidden border-t border-white/8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_50%,rgba(140,82,255,0.10)_0%,transparent_70%)] pointer-events-none" />
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center relative z-10">
          <div className="inline-flex items-center gap-2 bg-[#8c52ff]/15 border border-[#8c52ff]/30 rounded-full px-4 py-2 mb-6">
            <i className="ri-calendar-check-line text-[#8c52ff] text-sm" />
            {isEditing ? (
              <input 
                type="text" 
                value={content.calendar.badge} 
                onChange={(e) => setContent({...content, calendar: {...content.calendar, badge: e.target.value}})}
                className="bg-transparent border border-purple-500/50 rounded px-2 py-0.5 text-[#8c52ff] text-xs font-bold uppercase tracking-widest focus:outline-none"
              />
            ) : (
              <span className="text-[#8c52ff] text-xs font-bold uppercase tracking-widest">{content.calendar.badge}</span>
            )}
          </div>
          
          {isEditing ? (
            <div className="max-w-xl mx-auto space-y-3 mb-5">
              <div className="flex gap-2">
                <input 
                  type="text" 
                  value={content.calendar.titlePrefix} 
                  onChange={(e) => setContent({...content, calendar: {...content.calendar, titlePrefix: e.target.value}})}
                  className="w-1/2 bg-white/5 border border-purple-500/50 rounded-lg px-4 py-2 text-white text-3xl font-black text-right focus:outline-none"
                />
                <input 
                  type="text" 
                  value={content.calendar.titleHighlight} 
                  onChange={(e) => setContent({...content, calendar: {...content.calendar, titleHighlight: e.target.value}})}
                  className="w-1/2 bg-white/5 border border-purple-500/50 rounded-lg px-4 py-2 text-[#FF2DAA] text-3xl font-black text-left focus:outline-none"
                />
              </div>
              <textarea 
                value={content.calendar.subtitle} 
                onChange={(e) => setContent({...content, calendar: {...content.calendar, subtitle: e.target.value}})}
                className="w-full bg-white/5 border border-purple-500/50 rounded-lg px-4 py-2 text-white/60 text-lg text-center focus:outline-none h-20"
              />
            </div>
          ) : (
            <>
              <h2 className="text-3xl md:text-5xl font-black text-white mb-5 leading-tight">
                {content.calendar.titlePrefix}{' '}
                <span className="bg-gradient-to-r from-[#8c52ff] to-[#FF2DAA] bg-clip-text text-transparent">
                  {content.calendar.titleHighlight}
                </span>
              </h2>
              <p className="text-white/60 text-lg mb-10 max-w-2xl mx-auto leading-relaxed">
                {content.calendar.subtitle}
              </p>
            </>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-10">
            {[
              { icon: 'ri-time-line', title: t('contact.calendar.items.i1.title'), desc: t('contact.calendar.items.i1.desc'), color: '#8c52ff' },
              { icon: 'ri-video-line', title: t('contact.calendar.items.i2.title'), desc: t('contact.calendar.items.i2.desc'), color: '#00c2ff' },
              { icon: 'ri-gift-line', title: t('contact.calendar.items.i3.title'), desc: t('contact.calendar.items.i3.desc'), color: '#FF2DAA' },
            ].map((item) => (
              <div
                key={item.title}
                className="bg-white/3 border border-white/8 rounded-2xl p-6 flex flex-col items-center gap-3"
              >
                <div
                  className="w-12 h-12 flex items-center justify-center rounded-xl"
                  style={{ backgroundColor: `${item.color}20`, border: `1px solid ${item.color}40` }}
                >
                  <i className={`${item.icon} text-xl`} style={{ color: item.color }} />
                </div>
                <p className="text-white font-bold text-base">{item.title}</p>
                <p className="text-white/50 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>

          {isEditing ? (
            <div className="max-w-md mx-auto space-y-3">
              <input 
                type="text" 
                value={content.calendar.cta} 
                onChange={(e) => setContent({...content, calendar: {...content.calendar, cta: e.target.value}})}
                className="w-full bg-white/5 border border-purple-500/50 rounded-lg px-4 py-2 text-white text-center font-bold focus:outline-none"
                placeholder="Texto del botón"
              />
              <input 
                type="text" 
                value={content.calendar.link} 
                onChange={(e) => setContent({...content, calendar: {...content.calendar, link: e.target.value}})}
                className="w-full bg-white/5 border border-purple-500/50 rounded-lg px-4 py-2 text-white/50 text-center text-sm focus:outline-none"
                placeholder="Enlace (ej: https://stan.store/...)"
              />
            </div>
          ) : (
            <a
              href={content.calendar.link}
              target="_blank"
              rel="noopener noreferrer"
              className="whitespace-nowrap inline-flex items-center gap-3 bg-gradient-to-r from-[#8c52ff] to-[#FF2DAA] text-white font-bold px-10 py-4 rounded-full hover:opacity-90 transition-opacity duration-300 cursor-pointer text-base"
            >
              <i className="ri-calendar-line text-lg" />
              {content.calendar.cta}
              <i className="ri-arrow-right-line" />
            </a>
          )}
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
